// mParivahan / VAHAN connector.
// MoRTH does not expose a public read-API for vehicle data, fastag balance,
// or e-challans. Apps must accept a JSON / CSV export generated by the
// user from the mParivahan app's share-vehicle feature.

import type { ConnectorAdapter, ConnectorMode, ConnectorResult, ConnectorStatus } from './_types.ts';
import { err, nowIso, ok, shortHash } from './_types.ts';

export interface VehicleRecord {
  readonly registrationNo: string;
  readonly make: string;
  readonly model: string;
  readonly fuelType: 'PETROL' | 'DIESEL' | 'EV' | 'CNG' | 'HYBRID' | 'LPG' | 'OTHER';
  readonly registeredOn: string;
  readonly ownerName: string;
  readonly chassisLast5: string;
  readonly engineLast5: string;
  readonly fitnessUpto: string;
  readonly insuranceUpto: string;
  readonly pucUpto: string;
  readonly rtoCode: string;
  readonly state: string;
}

export interface FastagSnapshot {
  readonly vehicleRegistrationNo: string;
  readonly tagId: string;
  readonly bankIssuer: string;
  readonly balancePaise: number;
  readonly lastRechargedOn: string;
  readonly status: 'active' | 'inactive' | 'blacklisted';
}

export interface EChallan {
  readonly challanId: string;
  readonly vehicleRegistrationNo: string;
  readonly violation: string;
  readonly placeOfOffence: string;
  readonly amountPaise: number;
  readonly paidStatus: 'paid' | 'unpaid' | 'court-pending';
  readonly issuedOn: string;
}

export interface MparivahanSnapshot {
  readonly vehicles: readonly VehicleRecord[];
  readonly fastags: readonly FastagSnapshot[];
  readonly challans: readonly EChallan[];
  readonly fetchedAt: string;
}

export interface MparivahanConnectorStatus extends ConnectorStatus {
  readonly id: 'mparivahan';
  readonly vehicleCount: number;
  readonly unpaidChallans: number;
}

interface MparivahanPersist {
  readonly connectedAt: string;
  readonly fingerprintHash: string;
  readonly vehicleCount: number;
  readonly unpaidChallans: number;
  readonly snapshot: MparivahanSnapshot;
}

const STORE_KEY = 'empire/connectors/mparivahan';

export class MparivahanAdapter
  implements ConnectorAdapter<MparivahanConnectorStatus, MparivahanSnapshot>
{
  readonly id = 'mparivahan';
  readonly displayName = 'mParivahan · VAHAN';
  readonly displayNameHi = 'एम-परिवहन / वाहन';
  readonly dpdpCategory = 'wealth-vehicle-records';
  readonly mode: ConnectorMode;
  #pendingExport: string | null = null;

  constructor(mode: ConnectorMode = 'test') {
    this.mode = mode;
  }

  async getStatus(): Promise<MparivahanConnectorStatus> {
    const persisted = readPersist();
    return {
      id: this.id,
      mode: this.mode,
      connected: persisted !== null,
      lastSyncAt: persisted?.connectedAt ?? null,
      principalIdHash: persisted?.fingerprintHash ?? null,
      vehicleCount: persisted?.vehicleCount ?? 0,
      unpaidChallans: persisted?.unpaidChallans ?? 0,
      notes: 'Upload mParivahan share-vehicle export (JSON/CSV). No public read-API.',
    };
  }

  async connect(): Promise<ConnectorResult<MparivahanConnectorStatus>> {
    return ok(await this.getStatus());
  }

  async disconnect(): Promise<void> {
    if (typeof localStorage !== 'undefined') localStorage.removeItem(STORE_KEY);
  }

  queueExport(exportText: string): void {
    this.#pendingExport = exportText;
  }

  async importPayload(): Promise<ConnectorResult<MparivahanSnapshot>> {
    if (!this.#pendingExport) {
      const mock = this.mockPayload();
      await this.#persist(mock);
      return ok(mock);
    }
    try {
      const parsed = parseExport(this.#pendingExport);
      if (!parsed) {
        return err({ code: 'parse', message: 'Could not parse mParivahan export.', retriable: false });
      }
      await this.#persist(parsed);
      return ok(parsed);
    } catch (e) {
      return err({ code: 'parse', message: e instanceof Error ? e.message : 'parse-error', retriable: false });
    }
  }

  mockPayload(): MparivahanSnapshot {
    return {
      vehicles: [{
        registrationNo: 'DL3CBA1142',
        make: 'Honda',
        model: 'City ZX CVT',
        fuelType: 'PETROL',
        registeredOn: '2021-07-04',
        ownerName: 'Aditya Sharma',
        chassisLast5: 'X1042',
        engineLast5: 'A0911',
        fitnessUpto: '2036-07-03',
        insuranceUpto: '2026-07-04',
        pucUpto: '2026-08-15',
        rtoCode: 'DL-3',
        state: 'Delhi',
      }],
      fastags: [{
        vehicleRegistrationNo: 'DL3CBA1142',
        tagId: 'FT-04*****1142',
        bankIssuer: 'ICICI Bank',
        balancePaise: 1850_00,
        lastRechargedOn: '2026-05-08',
        status: 'active',
      }],
      challans: [],
      fetchedAt: nowIso(),
    };
  }

  async #persist(snap: MparivahanSnapshot): Promise<void> {
    if (typeof localStorage === 'undefined') return;
    const fingerprintHash = await shortHash(snap.vehicles.map((v) => v.registrationNo).join('|'));
    const unpaid = snap.challans.filter((c) => c.paidStatus === 'unpaid').length;
    const rec: MparivahanPersist = {
      connectedAt: snap.fetchedAt,
      fingerprintHash,
      vehicleCount: snap.vehicles.length,
      unpaidChallans: unpaid,
      snapshot: snap,
    };
    localStorage.setItem(STORE_KEY, JSON.stringify(rec));
  }
}

function readPersist(): MparivahanPersist | null {
  if (typeof localStorage === 'undefined') return null;
  const raw = localStorage.getItem(STORE_KEY);
  if (!raw) return null;
  try { return JSON.parse(raw) as MparivahanPersist; } catch { return null; }
}

function parseExport(input: string): MparivahanSnapshot | null {
  const trimmed = input.trim();
  if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
    try {
      const json = JSON.parse(trimmed) as MparivahanSnapshot;
      return json;
    } catch {
      return null;
    }
  }
  // CSV-shaped export: vehicles only, single-line header.
  const lines = trimmed.split(/\r?\n/);
  const header = (lines[0] ?? '').toLowerCase().split(',').map((c) => c.trim());
  const vehicleIdx = header.indexOf('registration_no');
  if (vehicleIdx < 0) return null;
  const vehicles: VehicleRecord[] = [];
  for (let i = 1; i < lines.length; i++) {
    const row = (lines[i] ?? '').split(',').map((c) => c.trim());
    const reg = row[vehicleIdx];
    if (!reg) continue;
    vehicles.push({
      registrationNo: reg,
      make: row[header.indexOf('make')] ?? '',
      model: row[header.indexOf('model')] ?? '',
      fuelType: (row[header.indexOf('fuel_type')] ?? 'OTHER').toUpperCase() as VehicleRecord['fuelType'],
      registeredOn: row[header.indexOf('registered_on')] ?? '',
      ownerName: row[header.indexOf('owner_name')] ?? '',
      chassisLast5: row[header.indexOf('chassis_last5')] ?? '',
      engineLast5: row[header.indexOf('engine_last5')] ?? '',
      fitnessUpto: row[header.indexOf('fitness_upto')] ?? '',
      insuranceUpto: row[header.indexOf('insurance_upto')] ?? '',
      pucUpto: row[header.indexOf('puc_upto')] ?? '',
      rtoCode: row[header.indexOf('rto_code')] ?? '',
      state: row[header.indexOf('state')] ?? '',
    });
  }
  return { vehicles, fastags: [], challans: [], fetchedAt: nowIso() };
}

export function makeMparivahanConnector(mode: ConnectorMode): MparivahanAdapter {
  return new MparivahanAdapter(mode);
}
